import { EquipmentAttributeDefinition } from "domain/entities/equipment-attribute-definition";
import { Equipment } from "domain/entities/equipment";
import { EquipmentType } from "domain/entities/equipment-type";

type ParsedEquipmentAttributes = Record<string, unknown>;

const hasFilledValue = (value: unknown) => {
  return value !== undefined && value !== null && String(value).trim() !== "";
};

export const parseEquipmentAttributes = (
  attributes?: Equipment["attributes"] | ParsedEquipmentAttributes | null,
): ParsedEquipmentAttributes => {
  if (!attributes) {
    return {};
  }

  if (typeof attributes !== "string") {
    return attributes;
  }

  try {
    return JSON.parse(attributes || "{}");
  } catch {
    return {};
  }
};

export const getVehicleEquipmentType = (equipmentTypes?: EquipmentType[]) => {
  return equipmentTypes?.find(
    (equipmentType) => equipmentType.name.toLowerCase() === "viatura",
  );
};

export const isVehicleEquipment = (
  equipmentTypeId?: string,
  vehicleEquipmentTypeId?: string,
) => {
  return equipmentTypeId === vehicleEquipmentTypeId;
};

export const filterEquipmentsByVehicleType = (
  equipments: Equipment[] | undefined,
  vehicleEquipmentTypeId: string | undefined,
  showOnlyVehicles: boolean,
) => {
  return (
    equipments?.filter((equipment) => {
      const vehicle = isVehicleEquipment(
        equipment.equipmentTypeId,
        vehicleEquipmentTypeId,
      );

      return showOnlyVehicles ? vehicle : !vehicle;
    }) || []
  );
};

export const calculateEquipmentCompleteness = (
  equipment: Equipment,
  attributeDefinitions?: EquipmentAttributeDefinition[],
) => {
  const baseFields = [
    equipment.equipmentTypeId,
    equipment.brand,
    equipment.model,
    equipment.serialOrCarRegistration,
    equipment.status,
  ];

  let filledCount = baseFields.filter(hasFilledValue).length;
  let totalCount = baseFields.length;

  if (!equipment.equipmentTypeId || !attributeDefinitions?.length) {
    return totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 100;
  }

  const typeDefinitions = attributeDefinitions.filter(
    (definition) => definition.equipmentTypeId === equipment.equipmentTypeId,
  );

  if (!typeDefinitions.length) {
    return totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 100;
  }

  const attributes = parseEquipmentAttributes(equipment.attributes);

  typeDefinitions.forEach((definition) => {
    totalCount += 1;

    if (hasFilledValue(attributes[definition.name])) {
      filledCount += 1;
    }
  });

  return totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 100;
};
